<script>
import {
  arrow,
  computePosition,
  flip,
  offset,
  shift,
  autoUpdate
} from '@floating-ui/dom'

import JadeText from '../JadeText'
import PLACEMENT_ENUM from '../enums/alignment.enum'

export default {
  name: 'JadeTooltip',

  components: {
    JadeText
  },

  props: {
    initialOpen: Boolean,
    text: {
      type: String,
      required: true
    },
    placement: {
      type: String,
      default: 'bottom',
      validation: v => Object.values(PLACEMENT_ENUM).includes(v)
    }
  },

  data () {
    return {
      isVisible: false,
      referenceRef: null,
      floatingRef: null,
      arrowRef: null
    }
  },

  computed: {
    defineFloatingID () {
      return `floating-ui-${new Date().getTime()}`
    }
  },

  async mounted () {
    this.isVisible = this.initialOpen

    const { referenceRef, floatingRef, arrowRef } = this.$refs

    this.referenceRef = referenceRef
    this.floatingRef = floatingRef
    this.arrowRef = arrowRef

    this.isVisible && (await this.calculatePosition())
  },

  methods: {
    hide () {
      this.isVisible = false
    },

    show () {
      this.isVisible = true
      this.calculatePosition()
    },

    async calculatePosition () {
      const { x, y, middlewareData, placement } = await computePosition(
        this.referenceRef,
        this.floatingRef,
        {
          placement: this.placement,
          whileElementsMounted: autoUpdate,
          middleware: [
            offset(12),
            flip({
              fallbackAxisSideDirection: 'end',
              crossAxis: false
            }),
            shift(),
            arrow({ element: this.arrowRef, padding: 12 })
          ]
        }
      )

      Object.assign(this.floatingRef?.style ?? {}, {
        left: `${x}px`,
        top: `${y}px`
      })

      const { x: arrowX, y: arrowY } = middlewareData.arrow

      const opposedSide = {
        left: PLACEMENT_ENUM.RIGHT,
        right: PLACEMENT_ENUM.LEFT,
        bottom: PLACEMENT_ENUM.TOP,
        top: PLACEMENT_ENUM.BOTTOM
      }[placement.split('-')[0]]

      Object.assign(this.arrowRef?.style ?? {}, {
        left: arrowX ? `${arrowX}px` : '',
        top: arrowY ? `${arrowY}px` : '',
        bottom: '',
        right: '',
        [opposedSide]: '-4px'
      })
    }
  }
}
</script>

<template>
  <div class="jade-tooltip">
    <div
      ref="referenceRef"
      role="presentation"
      class="jade-tooltip__trigger inline-block"
      :aria-describedby="defineFloatingID"
      @blur="hide"
      @focus="show"
      @mouseenter="show"
      @mouseleave="hide"
    >
      <slot />
    </div>

    <div
      ref="floatingRef"
      role="tooltip"
      tabindex="-1"
      :id="defineFloatingID"
      :class="['absolute top-0 left-0 z-50', !isVisible && 'hidden']"
    >
      <div class="jade-tooltip__container">
        <div
          ref="arrowRef"
          aria-hidden="true"
          class="absolute h-[8px] w-[8px] rotate-45 bg-jade-background-neutral"
        ></div>
        <jade-text
          variant="text-small"
          weight="medium"
          color="on-brand"
        >
          {{ text }}
        </jade-text>
      </div>
    </div>
  </div>
</template>

<style scope>
@import '@stone-payments/jade/css/tooltip.css';
</style>
